import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:scale-[0.97] active:scale-[0.95]",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-card hover:bg-primary/90 hover:shadow-[inset_0_2px_8px_hsl(0_0%_0%/0.28)]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-[inset_0_2px_8px_hsl(0_0%_0%/0.22)]",
        outline:
          "border border-input bg-background hover:border-primary/40 hover:bg-primary-soft hover:text-primary hover:shadow-[inset_0_2px_6px_hsl(162_52%_8%/0.08)]",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:shadow-[inset_0_2px_6px_hsl(162_52%_8%/0.08)]",
        ghost: "hover:bg-primary-soft hover:text-primary hover:shadow-[inset_0_2px_6px_hsl(162_52%_8%/0.06)]",
        link: "scale-100 text-primary underline-offset-4 hover:scale-100 hover:underline active:scale-100",
        hero:
          "bg-primary text-primary-foreground shadow-card hover:bg-primary/90 hover:shadow-[inset_0_2px_8px_hsl(0_0%_0%/0.28)]",
        soft:
          "bg-primary-soft text-primary hover:bg-primary hover:text-primary-foreground hover:shadow-[inset_0_2px_8px_hsl(0_0%_0%/0.22)]",
        "ghost-outline":
          "border border-border bg-card text-foreground hover:border-primary/40 hover:text-primary hover:shadow-[inset_0_2px_6px_hsl(162_52%_8%/0.1)]",
        onPrimary:
          "bg-white text-primary shadow-card hover:bg-white/95 hover:shadow-[inset_0_2px_6px_hsl(162_52%_8%/0.12)]",
        ghostOnPrimary:
          "border border-white/30 bg-transparent text-primary-foreground hover:bg-white/10 hover:shadow-[inset_0_2px_6px_hsl(0_0%_0%/0.18)]",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-full px-4",
        lg: "h-11 rounded-full px-8",
        xl: "h-12 rounded-full px-8 text-base",
        icon: "h-10 w-10 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
